import React, { useEffect, useState } from 'react'
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  Chip,
  CircularProgress,
  Tooltip,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import CheckIcon from '@mui/icons-material/Check'
import DoneAllIcon from '@mui/icons-material/DoneAll'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import DisabledReason from '../system/DisabledReason'
import { ReviewChangeItem, ReviewChangeType, REVIEW_CHANGE_TYPE_LABELS, REVIEW_CHANGE_TYPE_ORDER } from '../../utils/reviewChanges'

const STORAGE_PREFIX = 'platinum:reviewChanges'

/** "Mark as reviewed" state is a client-side acknowledgement, not part of the
 *  sign-off record — it just declutters the active list within a review pass.
 *  Namespaced by dashboard + sign-off, so a fresh sign-off starts clean: the
 *  changes it produces have not been reviewed by anyone yet. */
function storageKey(dashboardId: string, signOffKey: string): string {
  return `${STORAGE_PREFIX}:${dashboardId}:${signOffKey}`
}

function readReviewed(key: string): Set<string> {
  try {
    const raw = window.localStorage.getItem(key)
    if (!raw) return new Set()
    const arr = JSON.parse(raw)
    return Array.isArray(arr) ? new Set(arr) : new Set()
  } catch {
    return new Set()
  }
}

function writeReviewed(key: string, ids: Set<string>): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(Array.from(ids)))
  } catch {
    // Best-effort — a full/unavailable localStorage just means the
    // acknowledgement doesn't persist across reloads this session.
  }
}

export interface ReviewChangesPanelProps {
  open: boolean
  onClose: () => void
  items: ReviewChangeItem[]
  isLoading?: boolean
  dashboardId: string
  /** Namespaces the "reviewed" acknowledgement to the current sign-off, e.g.
   *  String(config.sign_off?.signed_at ?? 'unsigned'). */
  signOffKey: string
  onJumpToItem: (item: ReviewChangeItem) => void
  onSignOff: () => void
  signOffPending?: boolean
  /** Why this viewer cannot sign off (they cannot edit the dashboard in place). */
  signOffDisabledReason?: string
}

const ReviewChangesPanel: React.FC<ReviewChangesPanelProps> = ({
  open,
  onClose,
  items,
  isLoading,
  dashboardId,
  signOffKey,
  onJumpToItem,
  onSignOff,
  signOffPending,
  signOffDisabledReason,
}) => {
  const key = storageKey(dashboardId, signOffKey)
  const [reviewedIds, setReviewedIds] = useState<Set<string>>(() => readReviewed(key))

  // Reset the acknowledgement set when the panel starts tracking a different
  // sign-off (a new one, or a dashboard switch) — see storageKey's doc.
  useEffect(() => {
    setReviewedIds(readReviewed(key))
  }, [key])

  const markReviewed = (id: string) => {
    setReviewedIds(prev => {
      const next = new Set(prev)
      next.add(id)
      writeReviewed(key, next)
      return next
    })
  }

  const markAllReviewed = () => {
    setReviewedIds(prev => {
      const next = new Set(prev)
      items.forEach(item => next.add(item.id))
      writeReviewed(key, next)
      return next
    })
  }

  const activeItems = items.filter(item => !reviewedIds.has(item.id))
  const hasMissingTable = items.some(item => item.type === 'missing_table')
  const reviewedCount = items.length - activeItems.length

  const grouped: Partial<Record<ReviewChangeType, ReviewChangeItem[]>> = {}
  activeItems.forEach(item => {
    (grouped[item.type] ||= []).push(item)
  })

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: 380, display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6">Review changes</Typography>
          <IconButton size="small" onClick={onClose} aria-label="Close review changes panel">
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {isLoading && activeItems.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : activeItems.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
              <CheckCircleIcon sx={{ fontSize: 32, mb: 1, opacity: 0.5 }} />
              <Typography variant="body2">
                {reviewedCount > 0 ? 'All changes reviewed.' : 'No changes since the last sign-off.'}
              </Typography>
            </Box>
          ) : (
            REVIEW_CHANGE_TYPE_ORDER.filter(type => grouped[type]?.length).map(type => (
              <Box key={type}>
                <Box sx={{ px: 2, py: 1, bgcolor: 'grey.50', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {type === 'missing_table' && <ErrorOutlineIcon sx={{ fontSize: 16, color: 'error.main' }} />}
                  <Typography variant="caption" fontWeight={600} color={type === 'missing_table' ? 'error.main' : 'text.secondary'}>
                    {REVIEW_CHANGE_TYPE_LABELS[type]} ({grouped[type]!.length})
                  </Typography>
                </Box>
                <List dense disablePadding>
                  {grouped[type]!.map(item => (
                    <ListItemButton
                      key={item.id}
                      onClick={() => onJumpToItem(item)}
                      sx={{ alignItems: 'flex-start', gap: 1 }}
                    >
                      <ListItemText
                        primary={item.message}
                        secondary={item.widgetTitle}
                        primaryTypographyProps={{ variant: 'body2' }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                      <Tooltip title="Mark as reviewed">
                        <IconButton
                          size="small"
                          aria-label={`Mark "${item.message}" as reviewed`}
                          onClick={(e) => { e.stopPropagation(); markReviewed(item.id) }}
                        >
                          <CheckIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    </ListItemButton>
                  ))}
                </List>
                <Divider />
              </Box>
            ))
          )}
        </Box>

        <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', display: 'flex', flexDirection: 'column', gap: 1 }}>
          {reviewedCount > 0 && (
            <Chip
              size="small"
              label={`${reviewedCount} reviewed`}
              sx={{ alignSelf: 'flex-start' }}
            />
          )}
          <Button
            size="small"
            startIcon={<DoneAllIcon />}
            onClick={markAllReviewed}
            disabled={activeItems.length === 0}
          >
            Mark all as reviewed
          </Button>
          {signOffDisabledReason ? (
            <DisabledReason reason={signOffDisabledReason}>
              <Button fullWidth variant="contained" color="success" startIcon={<CheckCircleIcon />}>
                Sign off
              </Button>
            </DisabledReason>
          ) : (
            <Tooltip title={hasMissingTable ? 'Resolve every missing table reference before signing off' : ''}>
              <span>
                <Button
                  fullWidth
                  variant="contained"
                  color="success"
                  startIcon={<CheckCircleIcon />}
                  onClick={onSignOff}
                  disabled={hasMissingTable || signOffPending}
                >
                  Sign off
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>
      </Box>
    </Drawer>
  )
}

export default ReviewChangesPanel
